import { WizardBackground } from "@/components/mentor-profile/wizard-background";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import type { MentorProfileDraft } from "@/lib/api/mentor-profile";
import type {
  DraftSaveState,
  UseMentorProfileDraftResult,
} from "@/lib/hooks/mentor-profile/use-mentor-profile-draft";
import { isConfirmInfoComplete } from "@/lib/mentor-profile/confirm-info-readiness";
import {
  GENDER_IDENTITY_OPTIONS,
  PRONOUN_OPTIONS,
  RACE_OPTIONS,
  normalizeRace,
  SEX_OPTIONS,
} from "@/lib/mentor-profile/identity-options";
import { PostHogEvents, useTrackEvent } from "@/lib/posthog-events";
import { cn, formatDate, toDateOnly } from "@/lib/utils";
import {
  applyNationalPhoneEdit,
  digitsOnly,
  formatNationalPhone,
  splitPhone,
  toE164,
  type PhoneCountryCode,
} from "@/lib/utils/phone";
import { Button, TextInput } from "@somethings-ui/web";
import { ArrowLeft, ClipboardCheck, Loader2, Pencil } from "lucide-react";
import { DateTime } from "luxon";
import { ReactNode, useState } from "react";

interface ConfirmInfoStepProps {
  draft: MentorProfileDraft;
  saveState: DraftSaveState;
  setField: UseMentorProfileDraftResult["setField"];
  mentorId: string;
  onContinue: () => void;
  onBack?: () => void;
}

const formatBirthday = (iso?: string) => {
  const day = toDateOnly(iso);
  return day ? formatDate(day) : "";
};

function FieldCell({
  label,
  value,
  hint,
  isEditing,
  onEdit,
  onDone,
  className,
  children,
}: {
  label: string;
  value: ReactNode;
  hint?: string;
  isEditing: boolean;
  onEdit: () => void;
  onDone: () => void;
  className?: string;
  children: ReactNode;
}) {
  return (
    <div className={cn("flex flex-col gap-1 bg-white px-5 py-3.5", className)}>
      <div className="flex items-start justify-between gap-3">
        <p className="font-body text-xs uppercase tracking-[0.3px] text-[#797774]">
          {label}
        </p>
        <button
          type="button"
          onClick={isEditing ? onDone : onEdit}
          className="flex shrink-0 items-center gap-1.5 rounded-full px-3 py-1 font-body text-sm text-[#797774] transition-colors hover:bg-[#f7f5f2]"
        >
          {isEditing ? (
            "Done"
          ) : (
            <>
              <Pencil className="size-3.5" />
              Edit
            </>
          )}
        </button>
      </div>
      {isEditing ? (
        children
      ) : (
        <>
          <p className="truncate font-body text-[15px] text-[#1f1e1d]">{value}</p>
          {hint && (
            <p className="font-body text-xs text-[#797774]/80">{hint}</p>
          )}
        </>
      )}
    </div>
  );
}

function SelectField({
  value,
  options,
  onChange,
}: {
  value?: string;
  options: readonly string[];
  onChange: (value: string) => void;
}) {
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger className="mt-1 h-9 rounded-lg border-[#e7e0d5] font-body text-[15px] text-[#1f1e1d]">
        <SelectValue placeholder="Select" />
      </SelectTrigger>
      <SelectContent>
        {options.map((option) => (
          <SelectItem key={option} value={option}>
            {option}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

export function ConfirmInfoStep({
  draft,
  saveState,
  setField,
  mentorId,
  onContinue,
  onBack,
}: ConfirmInfoStepProps) {
  const { track } = useTrackEvent();
  const [editing, setEditing] = useState<string | null>(null);
  const [phoneCountry, setPhoneCountry] = useState<PhoneCountryCode>("US");
  const [phoneDigits, setPhoneDigits] = useState("");

  const done = (fieldName: string) => {
    track(PostHogEvents.MENTOR_PROFILE_CONFIRM_INFO_FIELD_EDITED, {
      mentor_id: mentorId,
      field_name: fieldName,
    });
    setEditing(null);
  };
  const isEditing = (key: string) => editing === key;
  const edit = (key: string) => () => {
    if (key === "phone") {
      const parts = splitPhone(draft.phone ?? "");
      setPhoneCountry(parts.country);
      setPhoneDigits(parts.national);
    }
    setEditing(key);
  };
  const editedPhone = applyNationalPhoneEdit(phoneCountry, phoneDigits);
  const phoneInputValue = toE164(editedPhone)
    ? formatNationalPhone(editedPhone)
    : phoneDigits;

  const fullName = [draft.first_name, draft.last_name].filter(Boolean).join(" ");
  const phoneValid = Boolean(draft.phone && toE164(draft.phone));
  const canContinue = isConfirmInfoComplete(draft);

  return (
    <WizardBackground>
      <div className="relative z-10 flex w-full max-w-[672px] flex-col gap-7 rounded-[26px] border border-[#e5e0d8] bg-white px-8 py-10 shadow-[0px_8px_48px_0px_rgba(0,0,0,0.15)]">
        <div className="flex flex-col gap-4">
          <div className="flex size-14 items-center justify-center rounded-[22px] bg-[rgba(243,238,230,0.6)] text-[#1f1e1d]">
            <ClipboardCheck className="size-7" />
          </div>
          <div>
            <h1 className="font-heading text-[27px] font-bold leading-[1.12] tracking-[-0.675px] text-[#1f1e1d]">
              Quick check — does this look right?
            </h1>
            <p className="mt-2 font-body text-[15px] leading-relaxed text-[#797774]">
              We pulled these from your application — give them a quick look and
              fix anything that&apos;s off.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-px overflow-hidden rounded-[22px] bg-[#e7e0d5]">
          <FieldCell
            className="col-span-2"
            label="Name"
            value={fullName || "—"}
            isEditing={isEditing("name")}
            onEdit={edit("name")}
            onDone={() => done("name")}
          >
            <div className="mt-1 flex gap-2">
              <TextInput
                variant="standard"
                placeholder="First name"
                value={draft.first_name ?? ""}
                onChange={(e) => setField("first_name", e.target.value)}
              />
              <TextInput
                variant="standard"
                placeholder="Last name"
                value={draft.last_name ?? ""}
                onChange={(e) => setField("last_name", e.target.value)}
              />
            </div>
          </FieldCell>

          <FieldCell
            className="col-span-2"
            label="Email"
            value={draft.email || "—"}
            isEditing={isEditing("email")}
            onEdit={edit("email")}
            onDone={() => done("email")}
          >
            <TextInput
              variant="standard"
              type="email"
              className="mt-1"
              placeholder="you@example.com"
              value={draft.email ?? ""}
              onChange={(e) => setField("email", e.target.value)}
            />
          </FieldCell>

          <FieldCell
            label="Date of birth"
            value={formatBirthday(draft.birthday) || "—"}
            isEditing={isEditing("birthday")}
            onEdit={edit("birthday")}
            onDone={() => done("birthday")}
          >
            <input
              type="date"
              max={DateTime.now().toISODate() ?? undefined}
              value={toDateOnly(draft.birthday) ?? ""}
              onChange={(e) => setField("birthday", e.target.value)}
              className="mt-1 w-full rounded-lg border border-[#e7e0d5] px-3 py-2 font-body text-[15px] text-[#1f1e1d] focus:outline-none"
            />
          </FieldCell>

          <FieldCell
            label="Phone"
            value={formatNationalPhone(draft.phone ?? "") || "—"}
            hint={!phoneValid ? "Enter a valid phone number" : undefined}
            isEditing={isEditing("phone")}
            onEdit={edit("phone")}
            onDone={() => {
              const next = applyNationalPhoneEdit(phoneCountry, phoneDigits);
              if (next !== (draft.phone ?? "")) setField("phone", next);
              done("phone");
            }}
          >
            <TextInput
              variant="standard"
              type="tel"
              className="mt-1"
              placeholder="(555) 555-5555"
              value={phoneInputValue}
              onChange={(e) => {
                const digits = digitsOnly(e.target.value);
                setPhoneDigits(digits);
                setField("phone", applyNationalPhoneEdit(phoneCountry, digits));
              }}
            />
          </FieldCell>

          <FieldCell
            label="Sex"
            value={draft.sex || "—"}
            isEditing={isEditing("sex")}
            onEdit={edit("sex")}
            onDone={() => done("sex")}
          >
            <SelectField
              value={draft.sex}
              options={SEX_OPTIONS}
              onChange={(value) => setField("sex", value)}
            />
          </FieldCell>

          <FieldCell
            label="Gender identity"
            value={draft.gender_identity || "—"}
            isEditing={isEditing("gender_identity")}
            onEdit={edit("gender_identity")}
            onDone={() => done("gender_identity")}
          >
            <SelectField
              value={draft.gender_identity}
              options={GENDER_IDENTITY_OPTIONS}
              onChange={(value) => setField("gender_identity", value)}
            />
          </FieldCell>

          <FieldCell
            label="Pronouns"
            value={draft.pronouns || "—"}
            hint="Shows on your profile"
            isEditing={isEditing("pronouns")}
            onEdit={edit("pronouns")}
            onDone={() => done("pronouns")}
          >
            <SelectField
              value={draft.pronouns}
              options={PRONOUN_OPTIONS}
              onChange={(value) => setField("pronouns", value)}
            />
          </FieldCell>

          <FieldCell
            label="Race"
            value={normalizeRace(draft.race) || "—"}
            isEditing={isEditing("race")}
            onEdit={edit("race")}
            onDone={() => done("race")}
          >
            <SelectField
              value={normalizeRace(draft.race)}
              options={RACE_OPTIONS}
              onChange={(value) => setField("race", value)}
            />
          </FieldCell>
        </div>

        <div className="flex h-5 items-center gap-2 text-xs text-[#797774]">
          {saveState === "saving" ? (
            <>
              <Loader2 className="size-3.5 animate-spin" />
              Saving...
            </>
          ) : saveState === "saved" ? (
            <span>All changes saved</span>
          ) : null}
        </div>

        <div
          className={cn(
            "flex items-center",
            onBack ? "justify-between" : "justify-end",
          )}
        >
          {onBack ? (
            <button
              type="button"
              onClick={onBack}
              aria-label="Go back"
              className="flex size-11 items-center justify-center rounded-full border border-[#f2f2f2] bg-white text-[#1f1e1d] shadow-[0px_0px_8px_rgba(0,0,0,0.08)]"
            >
              <ArrowLeft className="size-5" />
            </button>
          ) : null}
          <Button onPress={onContinue} disabled={!canContinue}>
            Yep, this is right
          </Button>
        </div>
      </div>
    </WizardBackground>
  );
}
